import { css } from "@emotion/react";
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronsLeftIcon,
	ChevronsRightIcon,
	RefreshCwIcon,
} from "lucide-react";
import type { FwQueryState } from "../hooks/useFwQuery";
import { useTailwindMediaQueries } from "../hooks/useTailwindMediaQueries";
import { useThemeState } from "../hooks/useThemeState";
import { useTranslate } from "../hooks/useTranslate/useTranslate";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export interface FwQueryPagerProps<T> {
	query: FwQueryState<T>;
	showRefreshButton?: boolean;
	showItemCount?: boolean;
	onPageChange?: (pageNo: number) => void;
}

export function FwQueryPager<T>({
	query,
	showRefreshButton = true,
	showItemCount = true,
	onPageChange,
}: FwQueryPagerProps<T>) {
	const translate = useTranslate();
	const theme = useThemeState().get().theme;
	const mediaQueries = useTailwindMediaQueries();

	return (
		<div
			css={css`flex:0 0 auto;text-align:center;color:${theme.palette.siteTextPrimary};font-size:.8rem;`}
		>
			<div className="flex items-center-safe justify-center-safe border-t border-t-gray-200 dark:border-t-neutral-900 bg-(--header-background-color)">
				<Button
					variant="ghost"
					size="icon"
					disabled={query.firstBtnDisabled}
					aria-label="first page"
					className="mx-1"
					onClick={async () => {
						if (onPageChange) {
							onPageChange(1);
						} else {
							await query.firstPageAsync();
						}
					}}
				>
					<ChevronsLeftIcon size={22} />
				</Button>

				<Button
					variant="ghost"
					size="icon"
					disabled={query.prevBtnDisabled}
					aria-label="previous page"
					className="mx-1"
					onClick={async () => {
						if (onPageChange) {
							onPageChange(query.pageNo - 1);
						} else {
							await query.prevPageAsync();
						}
					}}
				>
					<ChevronLeftIcon size={22} />
				</Button>

				<div
					css={css`display:inline-flex;flex-direction:column;align-items:center; justify-content:center;height:48px;`}
				>
					<div css={css`white-space:nowrap;`}>
						<span>{translate("Page")}: </span>
						<Input
							aria-label="Page number"
							type="text"
							className="mx-1 text-center w-9"
							disabled={query.pageNoBtnDisabled || query.totalItems === 0}
							value={query.totalItems === 0 ? "-" : query.pageNo}
							onChange={async (e) => {
								const value = e.currentTarget.value;
								if (onPageChange) {
									onPageChange(+value);
								} else {
									await query.setPageNoAsync(+value, false);
								}
							}}
							onKeyDown={async (e) => {
								const value = (e.currentTarget as HTMLInputElement).value;
								const key = e.key;
								if (key === "Enter") {
									if (onPageChange) {
										onPageChange(+value);
									} else {
										await query.setPageNoAsync(+value, true);
									}
								}
							}}
						/>
						<span> of </span>
						<span>{query.totalItems === 0 ? "-" : query.totalPages}</span>
					</div>
				</div>

				<Button
					variant="ghost"
					size="icon"
					disabled={query.nextBtnDisabled}
					aria-label="next page"
					className="mx-1"
					onClick={async () => {
						if (onPageChange) {
							onPageChange(query.pageNo + 1);
						} else {
							await query.nextPageAsync();
						}
					}}
				>
					<ChevronRightIcon size={22} />
				</Button>

				<Button
					variant="ghost"
					size="icon"
					disabled={query.lastBtnDisabled}
					aria-label="last page"
					className="mx-1"
					onClick={async () => {
						if (onPageChange) {
							onPageChange(query.totalPages);
						} else {
							await query.lastPageAsync();
						}
					}}
				>
					<ChevronsRightIcon size={22} />
				</Button>

				{showItemCount && mediaQueries.minw_sm && (
					<div css={css`white-space:nowrap;font-size:.7rem;padding:4px 0;`}>
						{query.isPending && query.totalItems === 0
							? "Loading..."
							: `${query.totalItems.toLocaleString()} ${translate("items")}`}
					</div>
				)}

				{showRefreshButton && (
					<Button
						variant="ghost"
						size="icon"
						aria-label="refresh"
						className="mx-1"
						onClick={async () => {
							await query.loadAsync();
						}}
					>
						<RefreshCwIcon size={22} />
					</Button>
				)}
			</div>
			{showItemCount && !mediaQueries.minw_sm && (
				<div css={css`white-space:nowrap;font-size:.7rem;`}>
					{query.isPending && query.totalItems === 0
						? "Loading..."
						: `${query.totalItems.toLocaleString()} ${translate("items")}`}
				</div>
			)}
		</div>
	);
}
